<script lang="ts">
  // Renders a user's avatar (wiki/feature-user-profile.md AC-UP-3). A stock
  // avatar is a big emoji in a circle; an uploaded avatar is the image fetched
  // via the signed-GET avatar URL. Falls back to a deterministic funny stock
  // emoji when the server has assigned no avatar yet.
  import { requestAvatarUrl } from '$lib/api/profile';
  import {
    isUploadedAvatar,
    stockEmoji,
    defaultStockAvatarFor,
  } from '$lib/utils/profile/avatars';

  interface Props {
    /** The avatar value (stock id or uploaded object-key), or null. */
    avatar?: string | null;
    /** The user id — used to pick the deterministic default when unset. */
    userId: string;
    /** Diameter in rem. */
    size?: number;
  }

  let { avatar = null, userId, size = 4 }: Props = $props();

  let uploadedUrl = $state<string | null>(null);

  // Resolve an uploaded avatar to a signed GET URL once (re-runs when the
  // avatar value changes to a different uploaded key).
  $effect(() => {
    uploadedUrl = null;
    if (!isUploadedAvatar(avatar)) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await requestAvatarUrl();
        if (!cancelled) uploadedUrl = res.downloadUrl;
      } catch {
        // Leave the emoji fallback in place on any failure.
      }
    })();
    return () => {
      cancelled = true;
    };
  });

  const emoji = $derived(stockEmoji(avatar) ?? stockEmoji(defaultStockAvatarFor(userId)) ?? '🦊');
</script>

<span class="avatar" style={`--avatar-size:${size}rem`} aria-hidden="true">
  {#if uploadedUrl}
    <img src={uploadedUrl} alt="" />
  {:else}
    <span class="emoji">{emoji}</span>
  {/if}
</span>

<style lang="scss">
  .avatar {
    flex: 0 0 auto;
    width: var(--avatar-size);
    height: var(--avatar-size);
    border-radius: 50%;
    background: color-mix(in srgb, var(--ink-mute) 14%, transparent);
    border: 1px solid var(--line);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .emoji {
    font-size: calc(var(--avatar-size) * 0.55);
    line-height: 1;
  }
</style>
